/* eslint-disable camelcase */
import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { NpsData } from '../../../../types/npsTypes';
import { RespondReturn } from './NpsService.types';

export const useNpsResponse = (): RespondReturn => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<null | AxiosError>(null);
  const [isPatched, setIsPatched] = useState(false);

  const postResponse = useCallback(
    async ({ score, action, utm_source, utm_medium, utm_campaign, utm_content, surveyId }: NpsData) => {
      setIsLoading(true);
      const data = {
        score,
        action,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
      };
      try {
        const response = await apiClient.post<{ id: string }>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].surveys(surveyId),
          data,
        });
        setIsSubmitted(true);
        setError(null);
        setIsLoading(false);
        return response.data;
      } catch (e) {
        setIsSubmitted(false);
        setError(e as AxiosError);
        setIsLoading(false);
        return null;
      }
    },
    [],
  );

  const postResponseWithCustomerId = useCallback(
    async ({
      score,
      action,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      surveyId,
      customerId,
    }: NpsData & Required<Pick<NpsData, 'customerId'>>) => {
      setIsLoading(true);
      const data = {
        score,
        action,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
      };
      try {
        const response = await apiClient.post<{ id: string }>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].surveys(surveyId),
          data,
          params: { customerId },
        });
        setIsSubmitted(true);
        setError(null);
        setIsLoading(false);
        return response.data;
      } catch (e) {
        setIsSubmitted(false);
        setError(e as AxiosError);
        setIsLoading(false);
        return null;
      }
    },
    [],
  );

  const patchResponse = useCallback(
    async ({
      action,
      reason,
      responseId,
      score,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
    }: {
      responseId: string;
      action?: string;
      reason?: string;
      score?: string;
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_content?: string;
    }) => {
      setIsLoading(true);
      const data = {
        action,
        reason,
        score,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
      };

      try {
        await apiClient.patch<{ id: string }>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].surveys(responseId),
          data,
        });
        setIsPatched(true);
        setError(null);
        setIsLoading(false);
      } catch (e) {
        setIsPatched(false);
        setError(e as AxiosError);
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    postResponse,
    postResponseWithCustomerId,
    patchResponse,
    isSubmitted,
    isLoading,
    error,
    isPatched,
  };
};
